import React, { useEffect, useState } from "react";
import {
  Box,
  Select,
  MenuItem,
  FormControl,
  SelectChangeEvent,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Locale, SUPPORTED_LOCALES, isValidLocale, getLocalizedPath, setLanguageInStorage, getStoredLocaleFromStorage, DEFAULT_LOCALE, getLocaleFromUrl } from "../../utils/routing";
import { getAssetUrl, getFallbackUrl } from "../../utils/asset";
const StickyLanguageBar: React.FC = () => {
  const { i18n } = useTranslation();
  const { locale } = useParams<{ locale: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  // Initialize with priority: URL > localStorage > default "en"
  const getInitialLocale = (): Locale => {
    // Priority 1: URL locale (highest)
    if (isValidLocale(locale)) {
      return locale;
    }
    // Priority 2: localStorage
    const storedLocale = getStoredLocaleFromStorage();
    if (storedLocale) {
      return storedLocale;
    }
    // Priority 3: Default
    return DEFAULT_LOCALE;
  };

  const [currentLocale, setCurrentLocale] = useState<Locale>(getInitialLocale());

  useEffect(() => {
    // Priority: URL (highest) > localStorage > default "en"
    const urlLocale = getLocaleFromUrl(location.pathname);
    const storedLocale = getStoredLocaleFromStorage();
    
    // Determine active locale based on priority
    const activeLocale = urlLocale || storedLocale || DEFAULT_LOCALE;

    // If URL locale exists, it has highest priority - sync everything to URL locale
    if (urlLocale) {
      // Update localStorage to match URL locale (when URL is copied to new browser)
      if (storedLocale !== urlLocale) {
        setLanguageInStorage(urlLocale);
      }
      // Update i18n and state if needed
      if (currentLocale !== urlLocale) {
        setCurrentLocale(urlLocale);
        i18n.changeLanguage(urlLocale);
      }
      return;
    }

    // No URL locale - use stored locale or default
    // If stored locale differs from current, update everything
    if (activeLocale !== currentLocale) {
      setCurrentLocale(activeLocale);
      i18n.changeLanguage(activeLocale);
      setLanguageInStorage(activeLocale);
    }
  }, [locale, i18n, currentLocale, location.pathname, navigate]);

  const handleLanguageChange = (event: SelectChangeEvent<string>) => {
    const newLocale = event.target.value as Locale;
    
    // Validation: Check if locale is valid and different from current
    if (!isValidLocale(newLocale)) {
      console.warn(`Invalid locale: ${newLocale}. Supported locales: ${SUPPORTED_LOCALES.join(", ")}`);
      return;
    }
    
    if (newLocale === currentLocale) {
      return;
    }

    // Update localStorage (both keys: i18nextLng and lnc-i18n)
    setLanguageInStorage(newLocale);
    
    // Update i18n immediately
    i18n.changeLanguage(newLocale);
    
    // Update current locale state
    setCurrentLocale(newLocale);

    // Get current path without locale prefix
    const pathSegments = location.pathname.split("/").filter(Boolean);
    // Remove /user/ prefix if present, then remove locale if present
    let pathWithoutLocale = pathSegments.join("/").replace(/^user\//, "");
    if (isValidLocale(pathSegments[0])) {
      pathWithoutLocale = pathSegments.slice(1).join("/");
    }
    const cleanPath = pathWithoutLocale || "login";
    
    // Navigate to new locale path, preserving any query params (e.g. ?token=...)
    const newPath = getLocalizedPath(cleanPath, newLocale);
    const newPathWithSearch = location.search ? `${newPath}${location.search}` : newPath;
    navigate(newPathWithSearch, { replace: true });
  };

  const flagPath = (loc: Locale) =>
    loc === "en" ? "asset/flags/gb.png" : "asset/flags/es.png";

  const getFlag = (loc: Locale) => getAssetUrl(flagPath(loc));

  const onFlagError = (loc: Locale) =>
    (e: React.SyntheticEvent<HTMLImageElement>) => {
      const fb = getFallbackUrl(flagPath(loc));
      if (e.currentTarget.src !== fb) e.currentTarget.src = fb;
    };

  const getLanguageCode = (loc: Locale) => {
    return loc === "en" ? "EN" : "ES";
  };

  return (
    <>
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          width: "100%",
          height: "100%",
          zIndex: 1, // Low z-index so content can overlay it
          pointerEvents: "none", // Allow clicks to pass through to content below
        }}
      />
      <Box
        sx={{
          position: "absolute",
          top: "8px",
          right: "20px",
          zIndex: 1400, // High z-index so selector appears on top
          pointerEvents: "auto", // Enable clicks on the selector
        }}
      >
        <FormControl size="small" sx={{ minWidth: 80 }}>
          <Select
            value={currentLocale}
            onChange={handleLanguageChange}
            renderValue={(value) => (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <img
                  src={getFlag(value as Locale)}
                  onError={onFlagError(value as Locale)}
                  alt={value}
                  style={{
                    width: "20px",
                    height: "15px",
                    objectFit: "cover",
                  }}
                />
                <span style={{ fontSize: "14px", fontWeight: 500 }}>
                  {getLanguageCode(value as Locale)}
                </span>
              </Box>
            )}
            sx={{
              "& .MuiSelect-select": {
                padding: "6px 32px 6px 8px",
                display: "flex",
                alignItems: "center",
              },
              "& .MuiOutlinedInput-notchedOutline": {
                border: "none",
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                border: "none",
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                border: "none",
              },
              "& .MuiSelect-icon": {
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                top: "50%",
                transform: "translateY(-50%)",
              },
            }}
          IconComponent={(props) => (
            <Box
              {...props}
              sx={{
                width: 0,
                height: 0,
                borderLeft: "4px solid transparent",
                borderRight: "4px solid transparent",
                borderTop: "6px solid #666",
                marginRight: "8px",
                pointerEvents: "none",
                display: "flex",
                alignItems: "center",
              }}
            />
          )}
          MenuProps={{
            PaperProps: {
              sx: {
                "& .MuiMenuItem-root": {
                  fontSize: "14px",
                },
                zIndex: 1500, // Ensure dropdown menu appears above everything
              },
            },
          }}
        >
          {SUPPORTED_LOCALES.map((loc) => (
            <MenuItem key={loc} value={loc}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <img
                  src={getFlag(loc)}
                  onError={onFlagError(loc)}
                  alt={loc}
                  style={{
                    width: "20px",
                    height: "15px",
                    objectFit: "cover",
                  }}
                />
                <span style={{ fontSize: "14px" }}>
                  {getLanguageCode(loc)}
                </span>
              </Box>
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      </Box>
    </>
  );
};

export default StickyLanguageBar;

